/**
 * Installer phone GNSS → device position_seal (INSTALL_SEAL).
 * Soft≠ cell/LBS/GeoIP · Soft≠ board GNSS (this SKU has no chip) · Soft≠ guess.
 *
 * Accuracy gate: cell/LBS is hundreds of meters–km. Phone GNSS at the board
 * must report coords.accuracy ≤ GNSS_SEAL_MAX_ACCURACY_M.
 */

export const GNSS_SEAL_MAX_ACCURACY_M = 50;

export const LBS_SOURCES = [
  "GEOIP",
  "IP_GUESS",
  "CELL",
  "CELL_ID",
  "CELL_TOWER",
  "LBS",
  "MCC",
  "MCC_MNC",
  "CID",
  "WIFI_BSS",
  "BSS",
  "BASESTATION",
  "WIFI_RTT",
];

const LBS_SET = new Set(LBS_SOURCES);

export function sourceIsLbs(src) {
  return LBS_SET.has(String(src || "").trim().toUpperCase());
}

export function confQFromAccuracyM(accuracyM) {
  const acc = Number(accuracyM);
  if (!Number.isFinite(acc) || acc <= 0) return 0;
  if (acc <= 10) return 220;
  if (acc <= 25) return 200;
  if (acc <= GNSS_SEAL_MAX_ACCURACY_M) return 160;
  return 0;
}

export function admitOperatorGnssFix(input) {
  const lat = Number(input && input.lat);
  const lng = Number(input && input.lng);
  const acc = Number(input && input.accuracyM);
  const src = String((input && input.source) || "INSTALL_SEAL").trim().toUpperCase();
  if (sourceIsLbs(src)) {
    return { ok: false, reason: "lbs_refuse", source: src };
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, reason: "need_lat_lng" };
  }
  if (lat === 0 && lng === 0) {
    return { ok: false, reason: "null_island" };
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return { ok: false, reason: "oor" };
  }
  if (!Number.isFinite(acc) || acc <= 0) {
    return { ok: false, reason: "need_accuracy" };
  }
  if (acc > GNSS_SEAL_MAX_ACCURACY_M) {
    return { ok: false, reason: "accuracy_lbs", accuracyM: acc };
  }
  return {
    ok: true,
    lat,
    lng,
    accuracyM: acc,
    conf_q: confQFromAccuracyM(acc),
    source: "INSTALL_SEAL",
  };
}

export function gnssSealSay(r) {
  const reason = r && r.reason;
  if (reason === "already_sealed") {
    return "Already sealed · stand at the board to reseal from Locate";
  }
  if (reason === "accuracy_lbs") {
    return "Fix ±" + Math.round(Number(r.accuracyM) || 0)
      + " m is cell/LBS-class · stand next to the board · Soft≠ stamp";
  }
  if (reason === "permission") return "Location permission needed · stand at the board";
  if (reason === "no_geolocation") return "This browser has no GNSS";
  if (reason === "lbs_refuse") return "Cell / GeoIP / LBS source refused";
  if (reason === "need_accuracy") return "Fix had no accuracy · Soft≠ stamp";
  if (reason === "null_island") return "0,0 refused";
  if (reason === "cmd_refuse") return "position_seal refused";
  if (reason === "need_device") return "Claim a device first";
  if (reason === "geo_fail") return "Phone GNSS failed · retry outdoors at the board";
  if (r && r.ok) {
    return "Install seal · phone GNSS ±" + Math.round(Number(r.accuracyM) || 0)
      + " m · Soft≠ cell/GeoIP";
  }
  return "Location not sealed";
}

export function requestOperatorGnssFix() {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ ok: false, reason: "no_geolocation" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = pos && pos.coords;
        resolve(admitOperatorGnssFix({
          lat: c && c.latitude,
          lng: c && c.longitude,
          accuracyM: c && c.accuracy,
          source: "INSTALL_SEAL",
        }));
      },
      (err) => {
        const code = err && err.code;
        resolve({
          ok: false,
          reason: code === 1 ? "permission" : "geo_fail",
          detail: String((err && err.message) || ""),
        });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  });
}

export async function postDevicePositionSeal({ run, pathFn, deviceId, toast, fix }) {
  const did = String(deviceId || "").trim();
  if (!did) {
    const miss = { ok: false, reason: "need_device" };
    toast && toast(gnssSealSay(miss), "warn");
    return miss;
  }
  const admitted = fix && typeof fix === "object" && "ok" in fix
    ? fix
    : await requestOperatorGnssFix();
  if (!admitted || !admitted.ok) {
    toast && toast(gnssSealSay(admitted), "warn");
    return admitted || { ok: false, reason: "geo_fail" };
  }
  const out = await run(pathFn("post__devices_p_commands", { pathParams: [did] }), {
    method: "POST",
    body: {
      cmd: "position_seal",
      params: {
        lat: admitted.lat,
        lng: admitted.lng,
        conf_q: admitted.conf_q,
        source: "INSTALL_SEAL",
      },
    },
  });
  if (!out) {
    const refused = { ok: false, reason: "cmd_refuse" };
    toast && toast(gnssSealSay(refused), "bad");
    return refused;
  }
  const posted = { ...admitted, posted: true };
  toast && toast(gnssSealSay(posted), "go");
  return posted;
}
